// Brand colors
export const BRAND_COLORS = {
    darkBlue: '#003349',
    mediumBlue: '#00A7E1',
    mediumBlueAccessible: '#007FAD',
    lightBlue: '#B9DBE5',
    softGrey: '#C1C6C8',
    sage: '#CCC4A7',
    yellow: '#F6D44D',
    red: '#EA6852',
    green: '#B7CD96'
};

// Colors for Product Specific promos (cycling through these)
const PRODUCT_SPECIFIC_COLORS = [
    BRAND_COLORS.darkBlue,        // Dark blue
    BRAND_COLORS.mediumBlue,      // Bright blue
    '#8B6B5C',                    // Brown (complementary)
    BRAND_COLORS.softGrey,        // Grey
    '#6B8E7A',                    // Muted teal-green
    '#9B7BB8',                    // Muted purple (complementary)
    '#7AACB8',                    // Dusty blue
];

// Convert hex to RGB
function hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
}

// Create a lighter version of a color (for background)
export function lightenColor(hex, amount = 0.80) {
    const rgb = hexToRgb(hex);
    if (!rgb) return '#ffffff';

    const lightR = Math.round(rgb.r + (255 - rgb.r) * amount);
    const lightG = Math.round(rgb.g + (255 - rgb.g) * amount);
    const lightB = Math.round(rgb.b + (255 - rgb.b) * amount);

    return `rgb(${lightR}, ${lightG}, ${lightB})`;
}

// Determine if a promo is a holiday promo
function isHolidayPromo(promoName) {
    const name = promoName.toLowerCase();
    return name.includes('holiday') ||
        name.includes('christmas') ||
        name.includes('black friday') ||
        name.includes('cyber monday') ||
        name.includes('thanksgiving');
}

// Determine if a promo is an Amazon/Prime Day promo
function isAmazonPromo(promoName) {
    const name = promoName.toLowerCase();
    return name.includes('amazon') ||
        name.includes('prime day') ||
        name.includes('prime days');
}

// Get color for a promotion based on its type and name
// promoColorMap is used to ensure same promo name gets same color
export function getPromoColor(promo, promoColorMap, productSpecificIndex) {
    const promoName = promo.name;
    const promoType = promo.promoType;

    // Holiday promos get red (overrides type)
    if (isHolidayPromo(promoName)) {
        return BRAND_COLORS.red;
    }

    // Amazon/Prime Day promos get yellow (overrides type)
    if (isAmazonPromo(promoName)) {
        return BRAND_COLORS.yellow;
    }

    // Promo type specific colors
    if (promoType === 'Bundle Promo') {
        return BRAND_COLORS.sage;
    }
    if (promoType === 'Sitewide Sale') {
        return BRAND_COLORS.mediumBlueAccessible;
    }
    if (promoType === 'Tiered Savings Promo') {
        return BRAND_COLORS.green;
    }

    // For Product Specific promos, use consistent color per promo name
    if (promoColorMap.has(promoName)) {
        return promoColorMap.get(promoName);
    }

    // Assign a new color from the product specific palette
    const color = PRODUCT_SPECIFIC_COLORS[productSpecificIndex % PRODUCT_SPECIFIC_COLORS.length];
    promoColorMap.set(promoName, color);
    return color;
}

// Global map to track promo name -> color assignments across all weeks
let globalPromoColorMap = new Map();
let productSpecificCounter = 0;

// Reset the global color map (call this when promotions data changes)
export function resetPromoColorMap() {
    globalPromoColorMap = new Map();
    productSpecificCounter = 0;
}

// Pre-assign colors to all promotions to ensure consistency across weeks
export function initializePromoColors(allPromos) {
    resetPromoColorMap();

    // Sort promos by start date to ensure consistent color assignment
    const sortedPromos = [...allPromos].sort((a, b) =>
        new Date(a.startDate) - new Date(b.startDate)
    );

    // Assign colors to each unique promo
    const seenPromos = new Set();
    for (const promo of sortedPromos) {
        if (!seenPromos.has(promo.name)) {
            seenPromos.add(promo.name);

            // Skip promos that get fixed colors
            if (isHolidayPromo(promo.name) || isAmazonPromo(promo.name)) continue;
            if (promo.promoType === 'Bundle Promo' ||
                promo.promoType === 'Sitewide Sale' ||
                promo.promoType === 'Tiered Savings Promo') continue;

            // Assign color for Product Specific promos
            const color = PRODUCT_SPECIFIC_COLORS[productSpecificCounter % PRODUCT_SPECIFIC_COLORS.length];
            globalPromoColorMap.set(promo.name, color);
            productSpecificCounter++;
        }
    }
}

// Assign colors to all promotions in a week
export function assignPromoColors(promos) {
    if (!promos || promos.length === 0) return [];

    const coloredPromos = [];

    promos.forEach((promo) => {
        const color = getPromoColor(promo, globalPromoColorMap, productSpecificCounter);
        coloredPromos.push({
            ...promo,
            color: color,
            bgColor: lightenColor(color, 0.80)
        });
    });

    return coloredPromos;
}
